/**
 * Google Analytics 4, hard-gated behind analytics consent.
 *
 * The gtag.js script is injected only after the user has stored an
 * "accepted" choice (see utils/consent.js) — never on "declined" or on no
 * choice. There is no other GA loader in the app (the old unconditional
 * script in public/index.html was removed with this module).
 */

import {
  getConsent,
  onConsentChange,
  CONSENT_ACCEPTED,
  CONSENT_DECLINED,
} from './consent';

export const GA_MEASUREMENT_ID = 'G-XRJ359ZNJ2';

// Documented GA opt-out flag: when true, gtag drops all hits for this
// property without a reload — covers the accepted→declined switch mid-session.
const GA_DISABLE_FLAG = `ga-disable-${GA_MEASUREMENT_ID}`;

let scriptInjected = false;
let initialized = false;

const hasAccepted = () => getConsent()?.choice === CONSENT_ACCEPTED;

export function loadAnalytics() {
  if (scriptInjected) return;
  // Same dev behavior as before the consent gate: no GA outside production builds.
  if (process.env.NODE_ENV !== 'production') return;
  if (!hasAccepted()) return;

  scriptInjected = true;
  window[GA_DISABLE_FLAG] = false;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

/**
 * Safe gtag event call: a no-op whenever GA isn't loaded or consent isn't
 * accepted, so callers never need to check consent themselves.
 */
export function trackEvent(eventName, params = {}) {
  if (!scriptInjected || !hasAccepted() || typeof window.gtag !== 'function') return;
  window.gtag('event', eventName, { send_to: GA_MEASUREMENT_ID, ...params });
}

/**
 * Best-effort removal of GA cookies (_ga and _ga_*) for this domain and its
 * parent domains. Also cleans up cookies set before consent gating existed.
 */
export function deleteAnalyticsCookies() {
  try {
    const gaCookieNames = document.cookie
      .split(';')
      .map((cookie) => cookie.split('=')[0].trim())
      .filter((name) => name === '_ga' || name.startsWith('_ga_'));

    const host = window.location.hostname;
    const domains = [null, host];
    const parts = host.split('.');
    for (let i = 0; i < parts.length - 1; i++) {
      domains.push(`.${parts.slice(i).join('.')}`);
    }

    gaCookieNames.forEach((name) => {
      domains.forEach((domain) => {
        document.cookie =
          `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/` +
          (domain ? `; domain=${domain}` : '');
      });
    });
  } catch (error) {
    // Best-effort only — never let cookie cleanup break the app.
  }
}

/**
 * Call once at app boot. Loads GA if consent was accepted on a previous
 * visit, and reacts to consent changes for the rest of the session.
 */
export function initAnalytics() {
  if (initialized) return;
  initialized = true;

  if (hasAccepted()) {
    loadAnalytics();
  } else if (getConsent()?.choice === CONSENT_DECLINED) {
    // Clean up cookies left behind from before the consent gate shipped.
    deleteAnalyticsCookies();
  }

  onConsentChange((record) => {
    if (record.choice === CONSENT_ACCEPTED) {
      window[GA_DISABLE_FLAG] = false;
      loadAnalytics();
    } else {
      window[GA_DISABLE_FLAG] = true;
      deleteAnalyticsCookies();
    }
  });
}
